import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import Select from "react-select";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";

import ConditionalButton from "../../../../layout/conditional-button";
import PersonSearch from "../../../../layout/select/person-search";
import MatterSearch from "../../../../layout/select/matter-search";
import { get as getStaff } from "../../../../actions/staff";
import { analyze as analyzeClockImportFile, report as generateClockImportReport, apply as applyClockImport } from "../../../../actions/clock.imports";
import {
  addMinutes,
  IMPORT_FILE_ACCEPT,
  evaluateImportFormula,
  minutesBetween,
  normalizePhone,
  parseImportFile,
  toNumber,
} from "../../../../utils/imports";

const IMPORT_TYPES = [
  { label: "Call Log (Time)", value: "call_log_time" },
];

const STEPS = [
  "Select Import Type",
  "Upload File",
  "Analyze / Normalize File",
  "Map Columns",
  "Generate Import Report",
  "Review Rows",
  "Approve and Import",
  "Completion Summary",
];

const SOURCE_TYPES = [
  { label: "Column", value: "column" },
  { label: "Constant", value: "constant" },
  { label: "Composite", value: "composite" },
  { label: "Formula", value: "formula" },
  { label: "Lookup", value: "lookup" },
  { label: "Calculated", value: "calculated" },
  { label: "Default", value: "default" },
];

const MATTER_FALLBACK_OPTIONS = [
  { label: "Report as needing attention", value: "report" },
  { label: "Attach to person", value: "attach_to_person" },
  { label: "Most recent matter, then person", value: "most_recent_matter" },
];

const TARGET_FIELDS = [
  { key: "staffUuid", label: "Staff", required: true, sources: ["default", "column", "constant"] },
  { key: "clientOrLeadPersonUuid", label: "Client / Lead", required: true, sources: ["lookup", "column", "constant"] },
  { key: "matterUuid", label: "Matter", required: false, sources: ["default", "column", "constant"] },
  { key: "timeFrom", label: "Time From", required: true, sources: ["column", "composite", "formula", "constant"] },
  { key: "timeTo", label: "Time To", required: true, sources: ["calculated", "column", "composite", "formula", "constant"] },
  { key: "minutes", label: "Minutes", required: true, sources: ["default", "column", "formula", "constant"] },
  { key: "rate", label: "Rate", required: true, sources: ["default", "column", "formula", "constant"] },
  { key: "gross", label: "Gross", required: false, sources: ["default", "column", "formula", "constant"] },
  { key: "writeOff", label: "Write-Off", required: false, sources: ["constant", "column", "formula", "default"] },
  { key: "net", label: "Net", required: false, sources: ["default", "column", "formula", "constant"] },
  { key: "notes", label: "Notes", required: false, sources: ["column", "composite", "formula", "constant"] },
];

const FIELD_LABELS = Object.fromEntries(TARGET_FIELDS.map((field) => [field.key, field.label]));

const ERROR_TERMS = {
  staffUuid: ["staff"],
  personUuid: ["client or lead", "clients or leads", "phone lookup"],
  clientOrLeadPersonUuid: ["client or lead", "clients or leads", "phone lookup"],
  matterUuid: ["matter"],
  caseUuid: ["matter"],
  timeFrom: ["start time"],
  timeTo: ["end time", "calculated end time"],
  minutes: ["minutes"],
  rate: ["rate"],
  gross: ["gross", "gross/net"],
  writeOff: ["gross/net"],
  net: ["net", "gross/net"],
  notes: ["notes"],
};

const emptyMapping = (targetField, sourceType = "default") => ({
  targetField,
  sourceType,
  columnKey: null,
  constantValue: "",
  composite: {
    parts: [
      { type: "column", columnKey: null },
      { type: "constant", value: " " },
      { type: "column", columnKey: null },
    ],
    outputType: "string",
  },
  formula: { expression: "" },
  lookup: { method: "phone", columnKey: null, normalize: true },
});

const labelIncludes = (column, ...terms) => {
  const label = `${column.label || ""} ${column.key || ""}`.toLowerCase();
  return terms.some((term) => label.includes(term));
};

const pickColumn = (columns, ...terms) => columns.find((column) => labelIncludes(column, ...terms))?.key || null;

const nextSessionUuid = () => `time-import-${Date.now()}-${Math.round(Math.random() * 100000)}`;

const money = (value) => {
  const number = toNumber(value);
  return number == null ? "" : number.toFixed(2);
};

const REVIEW_TABLE_STYLE = { minWidth: 1380, tableLayout: "fixed" };
const REVIEW_HEADER_STYLE = { position: "sticky", top: 0, zIndex: 2, background: "#fff" };
const REVIEW_SOURCE_STYLE = { whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" };
const REVIEW_INPUT_STYLE = { minWidth: 0 };

const dataStartValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 ? Math.floor(number) : 1;
};

const isXlsxFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return name.endsWith(".xlsx") || type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
};

function buildDefaultMappings(columns) {
  const phone = pickColumn(columns, "phone", "caller", "number");
  return TARGET_FIELDS.map((field) => {
    const mapping = emptyMapping(field.key, "default");
    if (field.key === "clientOrLeadPersonUuid" && phone) {
      return { ...mapping, sourceType: "lookup", lookup: { method: "phone", columnKey: phone, normalize: true } };
    }
    if (field.key === "timeFrom") return { ...mapping, sourceType: "column", columnKey: pickColumn(columns, "start", "started", "from", "date") };
    if (field.key === "timeTo") {
      const timeTo = pickColumn(columns, "end", "ended", "to");
      return timeTo ? { ...mapping, sourceType: "column", columnKey: timeTo } : { ...mapping, sourceType: "calculated" };
    }
    if (field.key === "minutes") return { ...mapping, sourceType: "column", columnKey: pickColumn(columns, "minute", "duration", "length") };
    if (field.key === "rate") return { ...mapping, sourceType: "default" };
    if (field.key === "gross" || field.key === "net") return { ...mapping, sourceType: "default" };
    if (field.key === "writeOff") return { ...mapping, sourceType: "constant", constantValue: 0 };
    if (field.key === "notes") return { ...mapping, sourceType: "column", columnKey: pickColumn(columns, "note", "description", "subject", "call") };
    return mapping;
  });
}

function clearFieldErrors(errors, field) {
  const terms = ERROR_TERMS[field] || [FIELD_LABELS[field] || field];
  return (errors || []).filter((message) => {
    const lower = String(message || "").toLowerCase();
    return !terms.some((term) => lower.includes(String(term).toLowerCase()));
  });
}

function matchableText(value) {
  const text = String(value ?? "").trim();
  return text ? text : null;
}

function rowsToCorrections(rows) {
  return Object.fromEntries((rows || []).map((row) => [row.importRowId, {
    staffUuid: row.proposed?.staffUuid || null,
    clientOrLeadPersonUuid: row.proposed?.personUuid || row.proposed?.clientOrLeadPersonUuid || null,
    matterUuid: row.proposed?.matterUuid || row.proposed?.caseUuid || null,
    timeFrom: row.proposed?.timeFrom || null,
    timeTo: row.proposed?.timeTo || null,
    minutes: row.proposed?.minutes ?? null,
    rate: row.proposed?.rate ?? null,
    gross: row.proposed?.gross ?? null,
    writeOff: row.proposed?.writeOff ?? 0,
    net: row.proposed?.net ?? null,
    notes: row.proposed?.notes || "",
  }]));
}

function LocalDropzone({ file, onFile }) {
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    multiple: false,
    accept: IMPORT_FILE_ACCEPT,
    onDrop: (accepted) => onFile(accepted?.[0] || null),
  });

  return (
    <div>
      <div
        {...getRootProps()}
        style={{
          border: "2px dashed #cbd5e1",
          borderRadius: 8,
          padding: 18,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <input {...getInputProps()} />
        <div className="fw-bold mb-1">{isDragActive ? "Drop the import file here" : "Upload import file"}</div>
        <div className="small text-muted">
          {file ? `Selected: ${file.name}` : "CSV, JSON, or XLSX only. The file is analyzed temporarily and is not stored."}
        </div>
      </div>
      {fileRejections?.length ? (
        <Alert variant="danger" className="mt-3 mb-0">Only CSV, JSON, and XLSX files are accepted.</Alert>
      ) : null}
    </div>
  );
}

function WizardSteps({ step, disabled, onStep }) {
  return (
    <div className="d-flex flex-wrap border-bottom mb-3">
      {STEPS.map((label, index) => (
        <button
          key={label}
          type="button"
          className={`btn btn-link px-3 py-2 text-decoration-none border-0 border-bottom rounded-0 ${step === index ? "text-primary border-primary fw-semibold" : "text-muted"}`}
          disabled={disabled || index > step}
          onClick={() => onStep(index)}
          style={{
            borderBottomWidth: step === index ? 2 : 0,
            marginBottom: -1,
          }}
        >
          {index + 1}. {label}
        </button>
      ))}
    </div>
  );
}

function MappingRow({ columns, mapping, onChange, sampleRow }) {
  const target = TARGET_FIELDS.find((item) => item.key === mapping.targetField);
  const sourceOptions = SOURCE_TYPES.filter((item) => target.sources.includes(item.value));
  const columnOptions = columns.map((column) => ({ value: column.key, label: column.label }));
  const [testResult, setTestResult] = useState(null);

  const set = (updates) => onChange({ ...mapping, ...updates });
  const setNested = (name, updates) => set({ [name]: { ...mapping[name], ...updates } });

  const testFormula = () => {
    try {
      const value = evaluateImportFormula(mapping.formula?.expression || "", {
        row: sampleRow?.values || {},
        defaults: { billingIncrementMinutes: 6, defaultRate: null },
      });
      setTestResult({ success: true, value: String(value ?? "") });
    } catch (error) {
      setTestResult({ success: false, value: error?.message || "Invalid formula." });
    }
  };

  return (
    <Row className="g-2 align-items-start py-2 border-bottom">
      <Col md="3">
        <div className="fw-bold">
          {target.label}
          {target.required ? <span className="text-danger ms-1">*</span> : null}
        </div>
      </Col>
      <Col md="3">
        <div className="top-label">
          <Select
            value={sourceOptions.find((item) => item.value === mapping.sourceType)}
            options={sourceOptions}
            classNamePrefix="react-select"
            onChange={(option) => set({ sourceType: option?.value || "default" })}
          />
          <Form.Label>SOURCE</Form.Label>
        </div>
      </Col>
      <Col md="6">
        {mapping.sourceType === "column" ? (
          <div className="top-label">
            <Select
              value={columnOptions.find((item) => item.value === mapping.columnKey) || null}
              options={columnOptions}
              classNamePrefix="react-select"
              isClearable
              onChange={(option) => set({ columnKey: option?.value || null })}
            />
            <Form.Label>COLUMN</Form.Label>
          </div>
        ) : null}

        {mapping.sourceType === "constant" ? (
          <div className="top-label">
            <Form.Control
              value={mapping.constantValue ?? ""}
              onChange={(event) => set({ constantValue: event.target.value })}
            />
            <Form.Label>CONSTANT VALUE</Form.Label>
          </div>
        ) : null}

        {mapping.sourceType === "lookup" ? (
          <Row className="g-2">
            <Col md="4">
              <div className="top-label">
                <Form.Control value="find by phone" readOnly />
                <Form.Label>LOOKUP METHOD</Form.Label>
              </div>
            </Col>
            <Col md="8">
              <div className="top-label">
                <Select
                  value={columnOptions.find((item) => item.value === mapping.lookup?.columnKey) || null}
                  options={columnOptions}
                  classNamePrefix="react-select"
                  isClearable
                  onChange={(option) => setNested("lookup", { method: "phone", columnKey: option?.value || null, normalize: true })}
                />
                <Form.Label>PHONE COLUMN</Form.Label>
              </div>
            </Col>
          </Row>
        ) : null}

        {mapping.sourceType === "composite" ? (
          <Row className="g-2">
            <Col md="5">
              <Select
                value={columnOptions.find((item) => item.value === mapping.composite?.parts?.[0]?.columnKey) || null}
                options={columnOptions}
                classNamePrefix="react-select"
                placeholder="First column"
                isClearable
                onChange={(option) => {
                  const parts = [...(mapping.composite?.parts || [])];
                  parts[0] = { type: "column", columnKey: option?.value || null };
                  setNested("composite", { parts });
                }}
              />
            </Col>
            <Col md="2">
              <Form.Control
                value={mapping.composite?.parts?.[1]?.value ?? " "}
                onChange={(event) => {
                  const parts = [...(mapping.composite?.parts || [])];
                  parts[1] = { type: "constant", value: event.target.value };
                  setNested("composite", { parts });
                }}
              />
            </Col>
            <Col md="5">
              <Select
                value={columnOptions.find((item) => item.value === mapping.composite?.parts?.[2]?.columnKey) || null}
                options={columnOptions}
                classNamePrefix="react-select"
                placeholder="Second column"
                isClearable
                onChange={(option) => {
                  const parts = [...(mapping.composite?.parts || [])];
                  parts[2] = { type: "column", columnKey: option?.value || null };
                  setNested("composite", { parts });
                }}
              />
            </Col>
          </Row>
        ) : null}

        {mapping.sourceType === "formula" ? (
          <Row className="g-2">
            <Col>
              <div className="top-label">
                <Form.Control
                  value={mapping.formula?.expression || ""}
                  onChange={(event) => setNested("formula", { expression: event.target.value })}
                  placeholder="roundUpToIncrement(toNumber(row.length), defaults.billingIncrementMinutes)"
                />
                <Form.Label>FORMULA</Form.Label>
              </div>
              {testResult ? (
                <div className={`small mt-1 text-${testResult.success ? "success" : "danger"}`}>{testResult.value}</div>
              ) : null}
            </Col>
            <Col xs="auto">
              <Button type="button" variant="outline-primary" onClick={testFormula}>Test</Button>
            </Col>
          </Row>
        ) : null}

        {mapping.sourceType === "default" ? (
          <div className="text-muted small pt-2">Resolved by import defaults and existing billing rules.</div>
        ) : null}

        {mapping.sourceType === "calculated" ? (
          <div className="text-muted small pt-2">Calculated from Time From plus Minutes.</div>
        ) : null}
      </Col>
    </Row>
  );
}

function ReportStatus({ row }) {
  if (row.status === "error") return <Badge bg="danger">Error</Badge>;
  if (row.status === "warning") return <Badge bg="warning" text="dark">Warning</Badge>;
  return <Badge bg="success">Valid</Badge>;
}

function SourceValue({ label, value, normalized }) {
  const text = matchableText(value);
  if (!text) return <div className="small text-muted mt-1" style={REVIEW_SOURCE_STYLE}>{label}: blank</div>;
  return (
    <div className="small text-muted mt-1" style={REVIEW_SOURCE_STYLE} title={text}>
      {label}: <span className="fw-semibold">{text}</span>
      {normalized && normalized !== text ? <span> ({normalized})</span> : null}
    </div>
  );
}

function ReviewField({ label, children, sourceLabel, sourceValue, normalized, onApplyMatches }) {
  return (
    <div className="mb-2">
      <div className="small text-muted fw-semibold">{label}</div>
      {children}
      {sourceLabel ? <SourceValue label={sourceLabel} value={sourceValue} normalized={normalized} /> : null}
      {onApplyMatches ? (
        <Button size="sm" variant="link" className="p-0" onClick={onApplyMatches}>Apply matches</Button>
      ) : null}
    </div>
  );
}

function AccountingImportWorkflowBase({ show, onHide, getStaff, analyzeFile: analyzeXlsxFile, generateReport, applyImport }) {
  const [step, setStep] = useState(0);
  const [importType, setImportType] = useState(IMPORT_TYPES[0]);
  const [file, setFile] = useState(null);
  const [dataStart, setDataStart] = useState({ row: 1, column: 1 });
  const [parsed, setParsed] = useState(null);
  const [analyzePreviewLimit, setAnalyzePreviewLimit] = useState(5);
  const [mappings, setMappings] = useState([]);
  const [defaults, setDefaults] = useState({
    staffUuid: null,
    matterFallbackMode: "report",
    roundMinutes: true,
    billingIncrementMinutes: 6,
    defaultRate: "",
    sessionUuid: nextSessionUuid(),
  });
  const [staffOptions, setStaffOptions] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setStep(0);
    setImportType(IMPORT_TYPES[0]);
    setFile(null);
    setDataStart({ row: 1, column: 1 });
    setParsed(null);
    setAnalyzePreviewLimit(5);
    setMappings([]);
    setReportRows([]);
    setSummary(null);
    setError(null);
    setDefaults({
      staffUuid: null,
      matterFallbackMode: "report",
      roundMinutes: true,
      billingIncrementMinutes: 6,
      defaultRate: "",
      sessionUuid: nextSessionUuid(),
    });
  };

  const close = () => {
    reset();
    onHide();
  };

  const ensureStaff = async () => {
    if (staffOptions.length) return staffOptions;
    const rows = await getStaff({});
    const options = (rows || []).map((item) => ({
      value: item.uuid,
      label: item.Person?.displayName || [item.Person?.firstName, item.Person?.lastName].filter(Boolean).join(" ") || item.uuid,
      data: item,
    }));
    setStaffOptions(options);
    return options;
  };

  const analyzeFile = async () => {
    if (!file) {
      setError("Choose a CSV, JSON, or XLSX file first.");
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const nextParsed = isXlsxFile(file)
        ? await analyzeXlsxFile({ file, dataStart })
        : await parseImportFile(file, { dataStart });
      if (nextParsed?.success === false) {
        throw new Error(nextParsed?.error?.message || "Unable to analyze the import file.");
      }
      setParsed(nextParsed);
      setAnalyzePreviewLimit(5);
      setMappings(buildDefaultMappings(nextParsed.columns));
      await ensureStaff();
      return true;
    } catch (err) {
      setError(err?.message || "Unable to analyze the import file.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reportPayload = () => ({
    importType: importType?.value,
    rows: parsed?.rows || [],
    columns: parsed?.columns || [],
    mappings,
    defaults: {
      ...defaults,
      defaultRate: defaults.defaultRate === "" ? null : Number(defaults.defaultRate),
    },
    corrections: rowsToCorrections(reportRows),
  });

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await generateReport(reportPayload());
      if (!result?.success) {
        setError(result?.error?.message || "Unable to generate the import report.");
        return false;
      }
      setReportRows(result.rows || []);
      setSummary(result.summary || null);
      return true;
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    const selectedRowIds = reportRows.filter((row) => row.selected && row.status !== "error").map((row) => row.importRowId);
    if (!selectedRowIds.length) {
      setError("Select at least one valid row to import.");
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await applyImport({ ...reportPayload(), selectedRowIds });
      if (!result?.success) {
        setError(result?.error?.message || "Unable to import the selected rows.");
        return false;
      }
      setSummary(result.summary || null);
      return true;
    } finally {
      setBusy(false);
    }
  };

  const next = async () => {
    if (step === 1) {
      if (!(await analyzeFile())) return;
      setStep(2);
      return;
    }
    if (step === 4) {
      if (!(await generate())) return;
      setStep(5);
      return;
    }
    if (step === 6) {
      if (!(await approve())) return;
      setStep(7);
      return;
    }
    setStep(Math.min(STEPS.length - 1, step + 1));
  };

  const back = () => setStep(Math.max(0, step - 1));

  const goToStep = (targetStep) => {
    if (busy || targetStep > step) return;
    setError(null);
    setStep(targetStep);
  };

  const setMapping = (mapping) => {
    setMappings((prev) => prev.map((item) => item.targetField === mapping.targetField ? mapping : item));
  };

  const recalculateRow = (proposed, changedField, overrides = {}) => {
    const next = { ...proposed };
    const timeToCalculated = mappings.find((item) => item.targetField === "timeTo")?.sourceType === "calculated";
    if (timeToCalculated && (changedField === "timeFrom" || changedField === "minutes") && !overrides.timeTo) {
      next.timeTo = addMinutes(next.timeFrom, next.minutes);
    }
    if (!timeToCalculated && (changedField === "timeFrom" || changedField === "timeTo") && !overrides.minutes) {
      const minutes = minutesBetween(next.timeFrom, next.timeTo);
      if (minutes != null) next.minutes = minutes;
    }
    if ((["minutes", "timeFrom", "timeTo", "rate"].includes(changedField)) && !overrides.gross) {
      const minutes = toNumber(next.minutes);
      const rate = toNumber(next.rate);
      if (minutes != null && rate != null) next.gross = Number((rate * (minutes / 60)).toFixed(2));
    }
    if ((["minutes", "timeFrom", "timeTo", "rate", "gross", "writeOff"].includes(changedField)) && !overrides.net) {
      const gross = toNumber(next.gross);
      const writeOff = toNumber(next.writeOff) || 0;
      if (gross != null) next.net = Number((gross - writeOff).toFixed(2));
    }
    return next;
  };

  const updateReportRow = (rowId, field, value, meta = {}) => {
    setReportRows((prev) => prev.map((row) => {
      if (row.importRowId !== rowId) return row;
      const overrides = { ...(row.overrides || {}), [field]: true };
      const proposed = recalculateRow({ ...(row.proposed || {}), [field]: value }, field, overrides);
      if (field === "staffUuid") proposed.staff = meta.option ? { uuid: meta.option.value, label: meta.option.label } : null;
      if (field === "personUuid" || field === "clientOrLeadPersonUuid") {
        proposed.personUuid = value || null;
        proposed.clientOrLeadPersonUuid = value || null;
        proposed.person = meta.option ? { uuid: meta.option.value, label: meta.option.label } : null;
      }
      if (field === "matterUuid" || field === "caseUuid") {
        proposed.matterUuid = value || null;
        proposed.caseUuid = value || null;
        proposed.matter = meta.option ? { uuid: meta.option.value, label: meta.option.label } : null;
      }
      const errors = clearFieldErrors(row.errors, field);
      return {
        ...row,
        proposed,
        overrides,
        errors,
        status: errors.length ? "error" : (row.warnings || []).length ? "warning" : "valid",
        selected: errors.length ? false : row.selected,
      };
    }));
  };

  const toggleRow = (rowId) => {
    setReportRows((prev) => prev.map((row) => row.importRowId === rowId ? { ...row, selected: !row.selected } : row));
  };

  const mappingForField = (field) => mappings.find((item) => item.targetField === (field === "caseUuid" ? "matterUuid" : field === "personUuid" ? "clientOrLeadPersonUuid" : field));

  const sourceValueFor = (row, field) => {
    const mapping = mappingForField(field);
    if (mapping?.sourceType === "lookup" && mapping.lookup?.columnKey) return row.values?.[mapping.lookup.columnKey];
    if (mapping?.sourceType === "column" && mapping.columnKey) return row.values?.[mapping.columnKey];
    return null;
  };

  const sourceLabelFor = (field) => {
    const mapping = mappingForField(field);
    if (mapping?.sourceType === "lookup") return mapping.lookup?.method === "phone" ? "Phone" : "Lookup";
    if (mapping?.sourceType === "column") return "Source";
    return "Source";
  };

  const matchKeyFor = (row, field) => {
    if (["personUuid", "clientOrLeadPersonUuid"].includes(field)) {
      const phone = matchableText(row.matchKeys?.phone) || normalizePhone(sourceValueFor(row, field)) || matchableText(sourceValueFor(row, field));
      return phone ? `person-phone:${phone}` : null;
    }
    const sourceValue = matchableText(sourceValueFor(row, field));
    if (sourceValue) return `${field}:${sourceValue}`;
    const proposedValue = matchableText(row.proposed?.[field]);
    return proposedValue ? `${field}:${proposedValue}` : null;
  };

  const applyToMatches = (sourceRow, field) => {
    const key = matchKeyFor(sourceRow, field);
    const value = sourceRow.proposed?.[field] || sourceRow.proposed?.[field === "personUuid" ? "clientOrLeadPersonUuid" : field];
    if (!key || !value) {
      setError("Choose a value on a row with matching source data before applying matches.");
      return;
    }
    const option = field === "staffUuid"
      ? { value, label: sourceRow.proposed?.staff?.label }
      : field === "personUuid"
        ? { value, label: sourceRow.proposed?.person?.label }
        : { value, label: sourceRow.proposed?.matter?.label };

    setReportRows((prev) => prev.map((row) => {
      if (row.importRowId === sourceRow.importRowId || matchKeyFor(row, field) !== key) return row;
      const proposed = { ...(row.proposed || {}) };
      if (field === "staffUuid") {
        proposed.staffUuid = value;
        proposed.staff = sourceRow.proposed?.staff || null;
      } else if (field === "personUuid") {
        proposed.personUuid = value;
        proposed.clientOrLeadPersonUuid = value;
        proposed.person = sourceRow.proposed?.person || null;
      } else {
        proposed.matterUuid = value;
        proposed.caseUuid = value;
        proposed.matter = sourceRow.proposed?.matter || null;
      }
      const errors = clearFieldErrors(row.errors, field);
      return {
        ...row,
        proposed,
        overrides: { ...(row.overrides || {}), [field]: true },
        errors,
        status: errors.length ? "error" : (row.warnings || []).length ? "warning" : "valid",
        selected: !errors.length,
      };
    }));

    if (option?.value) setError(null);
  };

  const renderSelectType = () => (
    <Row className="g-3">
      <Col md="6">
        <div className="top-label">
          <Select
            value={importType}
            options={IMPORT_TYPES}
            classNamePrefix="react-select"
            onChange={setImportType}
          />
          <Form.Label>IMPORT TYPE</Form.Label>
        </div>
      </Col>
      <Col xs="12">
        <Alert variant="info" className="mb-0">
          Call Log (Time) imports create billable time entries after review and approval.
        </Alert>
      </Col>
    </Row>
  );

  const renderUpload = () => (
    <Row className="g-3">
      <Col xs="12">
        <LocalDropzone file={file} onFile={(nextFile) => {
          setFile(nextFile);
          setParsed(null);
          setAnalyzePreviewLimit(5);
          setMappings([]);
          setReportRows([]);
          setSummary(null);
          setError(null);
        }} />
      </Col>
      <Col xs="12">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="text-muted small fw-semibold">Data starts on:</div>
          <div className="top-label" style={{ width: 110 }}>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={dataStart.row}
              onChange={(event) => {
                setDataStart((prev) => ({ ...prev, row: dataStartValue(event.target.value) }));
                setParsed(null);
                setAnalyzePreviewLimit(5);
                setMappings([]);
                setReportRows([]);
                setSummary(null);
                setError(null);
              }}
            />
            <Form.Label>ROW</Form.Label>
          </div>
          <div className="text-muted">x</div>
          <div className="top-label" style={{ width: 110 }}>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={dataStart.column}
              onChange={(event) => {
                setDataStart((prev) => ({ ...prev, column: dataStartValue(event.target.value) }));
                setParsed(null);
                setAnalyzePreviewLimit(5);
                setMappings([]);
                setReportRows([]);
                setSummary(null);
                setError(null);
              }}
            />
            <Form.Label>COL</Form.Label>
          </div>
        </div>
      </Col>
    </Row>
  );

  const renderAnalyze = () => {
    const previewRows = (parsed?.rows || []).slice(0, analyzePreviewLimit);
    const remaining = Math.max(0, (parsed?.rows?.length || 0) - previewRows.length);
    return (
      <Row className="g-3">
        <Col xs="12">
          <Alert variant="info" className="mb-0">
            {parsed?.rows?.length || 0} rows and {parsed?.columns?.length || 0} columns detected.
            {parsed?.hasHeader ? " Headers were detected." : " Indexed column labels were assigned."}
          </Alert>
        </Col>
        <Col xs="12">
          <Table responsive bordered size="sm" className="mb-2">
            <thead>
              <tr>
                <th>Row</th>
                {parsed?.columns?.map((column) => (
                  <th key={column.key}>{column.label}<div className="text-muted small">{column.detectedType}</div></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row) => (
                <tr key={row.importRowId}>
                  <td>{row.rowIndex}</td>
                  {parsed.columns.map((column) => <td key={column.key}>{String(row.values[column.key] ?? "")}</td>)}
                </tr>
              ))}
            </tbody>
          </Table>
          {remaining ? (
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={() => setAnalyzePreviewLimit((value) => Math.min((parsed?.rows?.length || 0), value + 25))}
            >
              View more ({remaining} remaining)
            </Button>
          ) : null}
        </Col>
      </Row>
    );
  };

  const renderMapping = () => (
    <Row className="g-3">
      <Col md="4">
        <div className="top-label">
          <Select
            value={staffOptions.find((item) => item.value === defaults.staffUuid) || null}
            options={staffOptions}
            classNamePrefix="react-select"
            isClearable
            onChange={(option) => setDefaults((prev) => ({ ...prev, staffUuid: option?.value || null }))}
          />
          <Form.Label>DEFAULT STAFF</Form.Label>
        </div>
      </Col>
      <Col md="4">
        <div className="top-label">
          <Select
            value={MATTER_FALLBACK_OPTIONS.find((item) => item.value === defaults.matterFallbackMode)}
            options={MATTER_FALLBACK_OPTIONS}
            classNamePrefix="react-select"
            onChange={(option) => setDefaults((prev) => ({ ...prev, matterFallbackMode: option?.value || "report" }))}
          />
          <Form.Label>MATTER FALLBACK</Form.Label>
        </div>
      </Col>
      <Col md="2">
        <div className="top-label">
          <Form.Control
            type="number"
            value={defaults.billingIncrementMinutes}
            onChange={(event) => setDefaults((prev) => ({ ...prev, billingIncrementMinutes: Number(event.target.value) || 6 }))}
          />
          <Form.Label>INCREMENT</Form.Label>
        </div>
      </Col>
      <Col md="2">
        <Form.Check
          className="mt-2"
          type="switch"
          id="roundMinutes"
          label="Round minutes"
          checked={defaults.roundMinutes}
          onChange={(event) => setDefaults((prev) => ({ ...prev, roundMinutes: event.target.checked }))}
        />
      </Col>
      <Col xs="12">
        {mappings.map((mapping) => (
          <MappingRow
            key={mapping.targetField}
            columns={parsed?.columns || []}
            mapping={mapping}
            sampleRow={parsed?.sampleRows?.[0]}
            onChange={setMapping}
          />
        ))}
      </Col>
    </Row>
  );

  const renderGenerate = () => (
    <Row className="g-3">
      <Col xs="12">
        <Alert variant="info" className="mb-0">
          Generate the report to resolve staff, client or lead, matter fallback, billing increments, rates, and row-level validation.
        </Alert>
      </Col>
      {summary ? (
        <Col xs="12">
          <div className="d-flex gap-2 flex-wrap">
            <Badge bg="secondary">Total {summary.total}</Badge>
            <Badge bg="success">Valid {summary.valid}</Badge>
            <Badge bg="warning" text="dark">Warnings {summary.warnings}</Badge>
            <Badge bg="danger">Errors {summary.errors}</Badge>
          </div>
        </Col>
      ) : null}
    </Row>
  );

  const renderReport = () => (
    <div>
      <div className="d-flex gap-2 flex-wrap mb-3">
        <Badge bg="secondary">Rows {reportRows.length}</Badge>
        <Badge bg="success">Selected {reportRows.filter((row) => row.selected).length}</Badge>
        <Badge bg="danger">Errors {reportRows.filter((row) => row.status === "error").length}</Badge>
      </div>
      <div className="table-responsive" style={{ maxHeight: "calc(100vh - 340px)", overflow: "auto" }}>
        <Table bordered hover size="sm" className="mb-0 align-middle" style={REVIEW_TABLE_STYLE}>
          <colgroup>
            <col style={{ width: 74 }} />
            <col style={{ width: "32%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "9%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={REVIEW_HEADER_STYLE}>Row</th>
              <th style={REVIEW_HEADER_STYLE}>Staff / Client / Matter</th>
              <th style={REVIEW_HEADER_STYLE}>Time</th>
              <th style={REVIEW_HEADER_STYLE}>Billing</th>
              <th style={REVIEW_HEADER_STYLE}>Notes</th>
              <th style={REVIEW_HEADER_STYLE}>Status</th>
            </tr>
          </thead>
          <tbody>
            {reportRows.map((row) => (
              <tr key={row.importRowId} className={row.status === "error" ? "table-danger" : row.status === "warning" ? "table-warning" : ""}>
                <td>
                  <Form.Check checked={Boolean(row.selected)} disabled={row.status === "error"} onChange={() => toggleRow(row.importRowId)} />
                  <div className="small fw-semibold mt-1">{row.rowIndex}</div>
                </td>
                <td>
                  <ReviewField
                    label="Staff"
                    sourceLabel={sourceLabelFor("staffUuid")}
                    sourceValue={sourceValueFor(row, "staffUuid")}
                    onApplyMatches={() => applyToMatches(row, "staffUuid")}
                  >
                    <Select
                      value={staffOptions.find((item) => item.value === row.proposed?.staffUuid) || null}
                      options={staffOptions}
                      classNamePrefix="react-select"
                      onChange={(option) => updateReportRow(row.importRowId, "staffUuid", option?.value || null, { option })}
                      isClearable
                    />
                  </ReviewField>
                  <ReviewField
                    label="Client / Lead"
                    sourceLabel={sourceLabelFor("personUuid")}
                    sourceValue={sourceValueFor(row, "personUuid")}
                    normalized={row.matchKeys?.phone}
                    onApplyMatches={() => applyToMatches(row, "personUuid")}
                  >
                    <PersonSearch
                      value={row.proposed?.personUuid}
                      name="personUuid"
                      onChange={(event) => updateReportRow(row.importRowId, "personUuid", event.target.value, { option: event.target.item })}
                    />
                  </ReviewField>
                  <ReviewField
                    label="Matter"
                    sourceLabel={sourceLabelFor("matterUuid")}
                    sourceValue={sourceValueFor(row, "matterUuid")}
                    onApplyMatches={() => applyToMatches(row, "matterUuid")}
                  >
                    <MatterSearch
                      value={row.proposed?.matterUuid}
                      name="matterUuid"
                      onChange={(event) => updateReportRow(row.importRowId, "matterUuid", event.target.value, { option: event.target.item })}
                    />
                  </ReviewField>
                </td>
                <td>
                  <ReviewField label="From">
                    <Form.Control
                      size="sm"
                      style={REVIEW_INPUT_STYLE}
                      value={row.proposed?.timeFrom || ""}
                      onChange={(event) => updateReportRow(row.importRowId, "timeFrom", event.target.value)}
                    />
                  </ReviewField>
                  <ReviewField label={`To ${mappingForField("timeTo")?.sourceType === "calculated" ? "(Calc)" : ""}`}>
                    <Form.Control
                      size="sm"
                      style={REVIEW_INPUT_STYLE}
                      value={row.proposed?.timeTo || ""}
                      readOnly={mappingForField("timeTo")?.sourceType === "calculated"}
                      onChange={(event) => updateReportRow(row.importRowId, "timeTo", event.target.value)}
                    />
                  </ReviewField>
                  <ReviewField label="Minutes">
                    <Form.Control
                      size="sm"
                      type="number"
                      style={REVIEW_INPUT_STYLE}
                      value={row.proposed?.minutes ?? ""}
                      onChange={(event) => updateReportRow(row.importRowId, "minutes", Number(event.target.value))}
                    />
                  </ReviewField>
                </td>
                <td>
                  <Row className="g-2">
                    <Col sm="6">
                      <ReviewField label="Rate">
                        <Form.Control
                          size="sm"
                          type="number"
                          style={REVIEW_INPUT_STYLE}
                          value={row.proposed?.rate ?? ""}
                          onChange={(event) => updateReportRow(row.importRowId, "rate", Number(event.target.value))}
                        />
                      </ReviewField>
                    </Col>
                    <Col sm="6">
                      <ReviewField label="Gross">
                        <Form.Control
                          size="sm"
                          type="number"
                          style={REVIEW_INPUT_STYLE}
                          value={money(row.proposed?.gross)}
                          onChange={(event) => updateReportRow(row.importRowId, "gross", Number(event.target.value))}
                        />
                      </ReviewField>
                    </Col>
                    <Col sm="6">
                      <ReviewField label="Write-Off">
                        <Form.Control
                          size="sm"
                          type="number"
                          style={REVIEW_INPUT_STYLE}
                          value={money(row.proposed?.writeOff)}
                          onChange={(event) => updateReportRow(row.importRowId, "writeOff", Number(event.target.value))}
                        />
                      </ReviewField>
                    </Col>
                    <Col sm="6">
                      <ReviewField label="Net">
                        <Form.Control
                          size="sm"
                          type="number"
                          style={REVIEW_INPUT_STYLE}
                          value={money(row.proposed?.net)}
                          onChange={(event) => updateReportRow(row.importRowId, "net", Number(event.target.value))}
                        />
                      </ReviewField>
                    </Col>
                  </Row>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    as="textarea"
                    rows={5}
                    style={{ ...REVIEW_INPUT_STYLE, resize: "vertical" }}
                    value={row.proposed?.notes || ""}
                    onChange={(event) => updateReportRow(row.importRowId, "notes", event.target.value)}
                  />
                </td>
                <td>
                  <ReportStatus row={row} />
                  {(row.errors || []).map((message) => <div key={message} className="small text-danger mt-1">{message}</div>)}
                  {(row.warnings || []).map((message) => <div key={message} className="small text-muted mt-1">{message}</div>)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );

  const renderApprove = () => {
    const selected = reportRows.filter((row) => row.selected && row.status !== "error");
    const blocked = reportRows.filter((row) => row.selected && row.status === "error");
    return (
      <Row className="g-3">
        <Col xs="12">
          <Alert variant={blocked.length ? "warning" : "info"} className="mb-0">
            {selected.length} valid row{selected.length === 1 ? "" : "s"} will be imported.
            {blocked.length ? ` ${blocked.length} selected row(s) still have blocking errors and will be skipped.` : ""}
          </Alert>
        </Col>
      </Row>
    );
  };

  const renderComplete = () => (
    <Row className="g-3">
      <Col xs="12">
        <Alert variant="success" className="mb-0">Import completed.</Alert>
      </Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Total Rows</div><div className="h4 mb-0">{summary?.total ?? 0}</div></div></Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Selected</div><div className="h4 mb-0">{summary?.selected ?? 0}</div></div></Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Imported</div><div className="h4 mb-0 text-success">{summary?.imported ?? 0}</div></div></Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Skipped / Failed</div><div className="h4 mb-0 text-warning">{Number(summary?.skipped || 0) + Number(summary?.failed || 0)}</div></div></Col>
    </Row>
  );

  const body = [
    renderSelectType,
    renderUpload,
    renderAnalyze,
    renderMapping,
    renderGenerate,
    renderReport,
    renderApprove,
    renderComplete,
  ][step];

  if (!show) return null;

  return (
    <Col xs="12">
      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="small-title mb-1">Import Time Entries</h2>
              <div className="text-muted small">Temporary import review for call-log time entries.</div>
            </div>
            <Badge bg="secondary">{STEPS[step]}</Badge>
          </div>

          <WizardSteps step={step} disabled={busy} onStep={goToStep} />

          {error ? <Alert variant="danger">{error}</Alert> : null}
          {busy ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : body()}

          <div className="d-flex justify-content-between gap-2 mt-4 pt-3 border-top">
            <div>
              <ConditionalButton enabledVariant="outline-primary" display enabled={!busy} onEnabledClick={close} icon="error-hexagon">
                cancel
              </ConditionalButton>
            </div>
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              {step > 0 && step < 7 ? (
                <ConditionalButton enabledVariant="outline-primary" display enabled={!busy} onEnabledClick={back} icon="arrow-left">
                  back
                </ConditionalButton>
              ) : null}
              {step < 7 ? (
                <ConditionalButton enabledVariant="outline-primary" display enabled={!busy} onEnabledClick={next} icon={step === 6 ? "upload" : "arrow-right"}>
                  {step === 4 ? "Generate" : step === 6 ? "Import" : "Next"}
                </ConditionalButton>
              ) : (
                <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={close} icon="check">
                  Done
                </ConditionalButton>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
}

AccountingImportWorkflowBase.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  getStaff: PropTypes.func.isRequired,
  analyzeFile: PropTypes.func.isRequired,
  generateReport: PropTypes.func.isRequired,
  applyImport: PropTypes.func.isRequired,
};

export const AccountingImportWorkflow = connect(null, {
  getStaff,
  analyzeFile: analyzeClockImportFile,
  generateReport: generateClockImportReport,
  applyImport: applyClockImport,
})(AccountingImportWorkflowBase);

export default function AccountingImportButton({ onStart, disabled }) {
  return (
    <ConditionalButton
      enabledVariant="outline-primary"
      tooltip="Import time entries"
      display
      enabled={!disabled}
      onEnabledClick={onStart}
      icon="upload"
    >
      Import
    </ConditionalButton>
  );
}

AccountingImportButton.propTypes = {
  onStart: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

AccountingImportButton.defaultProps = {
  disabled: false,
};
